/**
 * Cross-industry functions that DocumentIQ powers.
 *
 * Functions are organized by *what the workflow accomplishes*, not by
 * who is doing it. A manufacturer doing AP automation, a logistics
 * firm doing AP automation, and a law firm doing AP automation all
 * land on the same /solutions/function/ap-automation page — the
 * extraction mechanics are identical even when the industry context
 * differs. Each function cross-links to the industries that need it
 * and to the relevant document-type pages under /extract.
 *
 * Adding a new function: append an entry below, then update
 * solutions.ts so the industry pages that need it list its slug in
 * their `functionSlugs` array. The header dropdown, footer, and
 * cross-link sections all pull from this file automatically.
 */

export interface Function {
  slug: string
  /** Display name used in nav menus, headings, breadcrumbs. */
  name: string
  /** SEO title — appears in <title>, og:title, h1. */
  headline: string
  /** SERP meta description and list-page subhead. */
  description: string
  /** Long-form intro shown above the fold on the detail page. */
  heroDescription: string
  /**
   * Optional SEO overrides — replace the dynamic <title> / <meta description>
   * on this function's detail page when set. Fall back to
   * `${name} — AI Document Extraction` / `description` when omitted.
   */
  seoTitle?: string
  seoDescription?: string
  painPoints: { title: string; description: string }[]
  features: { title: string; description: string }[]
  /** slugs from caseStudies.ts — drives the Case Studies section. */
  caseStudySlugs: string[]
  /** slugs from documentTypes.ts — drives the related /extract links. */
  documentTypeSlugs: string[]
  /** slugs from solutions.ts — drives "Used in these industries" cross-link. */
  industrySlugs: string[]
  stats: { value: string; label: string }[]
  relatedLinks: { title: string; url: string; description: string }[]
}

export const functions: Function[] = [
  {
    slug: 'ap-automation',
    name: 'Accounts Payable Automation',
    headline: 'Capture supplier invoice data automatically and feed your ERP',
    description:
      'Extract structured data from supplier invoices, purchase orders, and receipts at scale. Cut AP cycle time, eliminate transcription errors, and pay on the right terms.',
    seoTitle: 'Document Automation Software for AP Teams | Algoscale',
    seoDescription:
      'Automate purchase order automation with AI-powered document automation software. Extract invoice and PO data accurately and speed up AP workflows.',
    heroDescription:
      'AP teams everywhere are stuck doing the same job: re-keying data from supplier invoices into the ERP, matching invoices against POs, chasing exceptions. The volume scales linearly with headcount and so does the error rate. DocumentIQ extracts invoice numbers, dates, vendor info, line items, and totals from any format — PDF, scanned, hand-stamped, multi-page — and feeds the structured output straight into your accounting system. Approval workflows stay where they are; the data entry just goes away.',
    painPoints: [
      {
        title: 'Manual coding from PDF invoices',
        description:
          'AP clerks re-key invoice number, date, vendor, GL code, and line items into the ERP. A team processing 5,000 invoices/month at 4 minutes each spends a full headcount on data entry alone.',
      },
      {
        title: 'Hundreds of vendor formats',
        description:
          'Every supplier ships their own invoice template. Traditional OCR with field-position templates breaks when a new vendor onboards or an existing one redesigns. Maintaining templates is a job in itself.',
      },
      {
        title: 'Line-item extraction is a coin toss',
        description:
          'Tables span multiple pages. Columns shift. Subtotals interrupt the flow. Most extraction tools either flatten everything or miss half the line items, forcing AP to verify manually.',
      },
      {
        title: 'Lost early-payment discounts',
        description:
          'Slow data entry pushes invoices past 2/10 net 30 windows. The 2% discount you negotiated is captured by nobody — invisible margin leakage compounding across thousands of invoices.',
      },
    ],
    features: [
      {
        title: 'Format-agnostic ingest',
        description:
          'Drop in PDFs, DOCX, or images. Annotate a handful of representative invoices per major vendor and the AI generalizes — no field-position templates to maintain.',
      },
      {
        title: 'Multi-row line items',
        description:
          'Extract every line item with quantity, unit price, tax, and total, even when tables span pages or use inconsistent column headers. Output a clean parent-child structure your ERP can consume.',
      },
      {
        title: 'GL coding via feedback',
        description:
          'When the model miscodes a line item, correct it inline. Subsequent invoices from the same vendor learn the pattern. Accuracy climbs into the high 90s after 2-3 feedback rounds per vendor.',
      },
      {
        title: 'Batch and queue',
        description:
          'Process the month-end backlog in parallel — thousands of invoices in minutes — or stream invoices in real-time from a shared mailbox. Real-time progress lets AP managers watch exceptions surface as they happen.',
      },
    ],
    caseStudySlugs: ['invoice-digitization'],
    documentTypeSlugs: ['invoices', 'purchase-orders', 'receipts'],
    industrySlugs: ['manufacturing', 'logistics', 'professional-services', 'financial-services'],
    stats: [
      { value: '15,000+', label: 'Invoices processed monthly' },
      { value: '4 min → 30 sec', label: 'AP cycle per invoice' },
      { value: '98%', label: 'Field-level accuracy after feedback' },
    ],
    relatedLinks: [
      { title: 'AI Consulting Services', url: 'https://algoscale.com/ai-consulting-service/', description: 'End-to-end AI strategy for AP automation' },
      { title: 'Data Engineering Services', url: 'https://algoscale.com/data-engineering-services/', description: 'Build the pipelines that connect extraction to your ERP' },
      { title: 'Generative AI Services', url: 'https://algoscale.com/generative-ai-services/', description: 'Apply LLMs to back-office document automation' },
      { title: 'Custom Software Development', url: 'https://algoscale.com/custom-software-development-services/', description: 'Connect DocumentIQ output to NetSuite, SAP, Oracle, or QuickBooks' },
    ],
  },
  {
    slug: 'contract-intelligence',
    name: 'Contract Intelligence',
    headline: 'Stop leaving revenue on the table — find every escalation clause you are entitled to invoke',
    description:
      'DocumentIQ extracts escalation clauses, renewal terms, and obligations from your customer and supplier contracts at scale — recovering unrealized revenue on the sell side and preventing cost surprises on the buy side.',
    seoTitle: 'AI Contract Automation Platform | Algoscale',
    seoDescription:
      'Automate contract review with AI. Extract clauses, renewal dates, obligations, and key terms from contracts to improve accuracy and speed.',
    heroDescription:
      'Most companies have escalation clauses in their customer contracts that entitle them to annual price increases — CPI adjustments, fixed percentage bumps, cost pass-throughs. But if nobody is tracking the trigger dates and invoking them, that revenue never materializes. Your margins erode silently while your own costs keep rising. DocumentIQ extracts escalation terms from both your sell-side and buy-side contracts, giving finance and account management a structured view of every increase they should be applying — and every increase coming from suppliers they should be negotiating.',
    painPoints: [
      {
        title: 'Uncollected sell-side escalations erode revenue',
        description:
          'Your customer contracts include annual escalation clauses you negotiated in good faith — but account managers are not applying them. A company with 300 client contracts averaging $150K, where 40% have 2.5% annual escalations, is leaving $450K+ in revenue on the table every year. Over three years, that compounds to $1.4M in unrealized top line.',
      },
      {
        title: 'Account managers avoid the pricing conversation',
        description:
          'Without a system surfacing the contractual basis for increases, AMs default to holding rates to avoid uncomfortable client conversations. When they can point to "Section 8.3, annual CPI adjustment," it shifts from a discretionary increase to a contractual obligation.',
      },
      {
        title: 'Buy-side escalations trigger without warning',
        description:
          'Supplier escalation clauses — CPI, fixed %, pass-through — are buried in legal language and activate automatically. Most companies discover the increase when the higher invoice arrives, too late to negotiate caps or alternatives.',
      },
      {
        title: 'No portfolio-level visibility on either side',
        description:
          'Finance cannot answer "What is our total escalation exposure next quarter?" or "Which client contracts have increases we have not applied?" Individual contract reviews cannot produce portfolio-level intelligence.',
      },
    ],
    features: [
      {
        title: 'Sell-side revenue recovery',
        description:
          'Extract escalation clauses from customer contracts — type, rate, trigger date, notice period. Generate quarterly reports showing which clients owe contractual increases, the dollar amount, and the clause reference.',
      },
      {
        title: 'Buy-side cost protection',
        description:
          'Extract supplier escalation terms — CPI indexes, fixed percentages, pass-through rights, caps. Surface upcoming increases 90 days in advance so procurement can negotiate, cap, or renegotiate.',
      },
      {
        title: 'Portfolio-level querying',
        description:
          'Chat across both sides: "Which client contracts have CPI escalations we have not applied in 12 months?", "Show supplier contracts with uncapped escalations over $100K", "What is our net escalation exposure in Q3?"',
      },
      {
        title: 'Annotation training for clause patterns',
        description:
          'Annotate 20-30 contracts to teach the AI where escalation clauses appear — often in schedules, appendices, or pricing exhibits rather than the main body. Works across varied contract templates.',
      },
    ],
    caseStudySlugs: ['contract-price-escalation'],
    documentTypeSlugs: ['contracts'],
    industrySlugs: ['professional-services', 'financial-services', 'manufacturing'],
    stats: [
      { value: '$450K+', label: 'Annual revenue recovered (sell-side)' },
      { value: '$2.4M', label: 'Cost savings (buy-side)' },
      { value: '97%', label: 'Extraction accuracy' },
    ],
    relatedLinks: [
      { title: 'Text Analytics', url: 'https://algoscale.com/text-analytics/', description: 'Extract insights from unstructured text in contracts and legal documents' },
      { title: 'AI Consulting Services', url: 'https://algoscale.com/ai-consulting-service/', description: 'End-to-end AI strategy for contract intelligence workflows' },
      { title: 'Generative AI Services', url: 'https://algoscale.com/generative-ai-services/', description: 'Leverage generative AI for document understanding and risk identification' },
      { title: 'Data Strategy Consulting', url: 'https://algoscale.com/data-strategy-consulting-services/', description: 'Build a data strategy around your contract portfolio' },
    ],
  },
  {
    slug: 'customs-trade-compliance',
    name: 'Customs & Trade Compliance',
    headline: 'Speed up clearances by extracting shipping doc data automatically',
    description:
      'Extract structured data from bills of lading, customs declarations, certificates of origin, and packing lists across every carrier and country — clearing shipments faster and surfacing duty exposure before it lands.',
    heroDescription:
      'Trade compliance teams handle 5-10 documents per shipment from a different carrier, broker, and origin country every time. Bills of lading from Maersk look nothing like Hapag-Lloyd. Customs declarations vary across 15+ countries. Certificates of origin use stamps that move between issuers. The data is structured, but the formats are not — so every shipment becomes a manual review. DocumentIQ extracts shipper, consignee, HS codes, declared values, container counts, and origin attestations from any of these formats in a single pipeline, with carrier- and country-specific annotations that handle layout differences automatically.',
    painPoints: [
      {
        title: 'Carrier-specific BOL formats',
        description:
          'Every carrier has its own bill of lading layout. The same fields — shipper, consignee, weight, piece count — appear in different positions on every template, breaking generic extraction tools.',
      },
      {
        title: 'Customs paperwork volume',
        description:
          'Each clearance requires data from 3-5 documents: commercial invoices with tariff line items, packing lists, certificates of origin. Brokers spend more time on data extraction than on actual compliance work.',
      },
      {
        title: 'Duty exposure surfaces too late',
        description:
          'Declared values, HS codes, and origin attestations determine duty liability. Without structured visibility before the shipment lands, compliance teams discover misclassification when the duty bill arrives.',
      },
      {
        title: 'Origin paperwork inconsistency',
        description:
          'Certificates of origin from different chambers and authorities use different layouts and stamp positions. Manual review is the only way to verify them — slow, error-prone, and a customs audit risk if wrong.',
      },
    ],
    features: [
      {
        title: 'Carrier annotations',
        description:
          'Train DocumentIQ once per major carrier — Maersk, MSC, Hapag-Lloyd, ONE — and it generalizes across new BOL revisions. New carriers onboard in minutes with 5-10 annotated examples.',
      },
      {
        title: 'Multi-document workflows',
        description:
          'Group commercial invoice + packing list + COO + declaration into a single shipment record. Cross-validate that the declared value and HS codes match across all four documents — flag mismatches before they hit customs.',
      },
      {
        title: 'Country-specific declaration extraction',
        description:
          'Built-in field templates for major customs declaration formats — US 7501, EU SAD, UK CDS — with annotations for jurisdiction-specific fields like incoterm, country of origin, and duty calculations.',
      },
      {
        title: 'Real-time exception queue',
        description:
          'Confidence scores per field surface ambiguous extractions for human review. A trade compliance specialist resolves 20 exceptions per hour instead of typing in 200 shipments per hour.',
      },
    ],
    caseStudySlugs: ['shipping-document-processing', 'customs-declaration-extraction'],
    documentTypeSlugs: ['bill-of-lading', 'customs-declarations', 'certificates-of-origin', 'packing-lists'],
    industrySlugs: ['logistics', 'manufacturing'],
    stats: [
      { value: '40%+', label: 'Faster customs clearance' },
      { value: '15+', label: 'Country formats supported' },
      { value: '99%', label: 'Field-level accuracy after carrier training' },
    ],
    relatedLinks: [
      { title: 'AI Consulting Services', url: 'https://algoscale.com/ai-consulting-service/', description: 'End-to-end AI strategy for trade compliance' },
      { title: 'Data Engineering Services', url: 'https://algoscale.com/data-engineering-services/', description: 'Connect extraction to your TMS, customs broker, or freight forwarder system' },
      { title: 'Generative AI Services', url: 'https://algoscale.com/generative-ai-services/', description: 'Apply LLMs to logistics document automation' },
      { title: 'Custom Software Development', url: 'https://algoscale.com/custom-software-development-services/', description: 'Build a unified shipment record from heterogeneous documents' },
    ],
  },
  {
    slug: 'quality-compliance',
    name: 'Quality & Compliance',
    headline: 'Verify mill certs, COAs, and spec sheets against your standards in seconds',
    description:
      'Extract material grades, test values, lot numbers, and certifications from mill test certificates and certificates of analysis — then auto-verify against your incoming specs before material hits the floor.',
    heroDescription:
      'Quality teams sit in a bottleneck of their own making. Every incoming shipment of regulated material — steel, alloys, pharma APIs, food-grade inputs — arrives with a paper trail of mill test certificates and certificates of analysis. The data inside is what determines whether the material gets accepted, rejected, or quarantined. But that data is locked in PDFs of varying layouts, and every shipment needs a human to read it. DocumentIQ extracts chemistry, mechanical properties, lot numbers, and certifications from MTCs and COAs in seconds, then cross-checks against your spec library so the only thing your QC team reviews is exceptions.',
    painPoints: [
      {
        title: 'Manual MTC and COA review',
        description:
          'QC inspectors read PDFs line-by-line, transcribing chemistry results and mechanical properties into spreadsheets. A single shipment takes 15-30 minutes per certificate.',
      },
      {
        title: 'Layout variance across mills',
        description:
          'Each producer issues MTCs in their own format. Tabular chemistry in one, prose in another, attached photos in a third. Template-based OCR fails the moment a new mill ships material.',
      },
      {
        title: 'Spec comparison happens too late',
        description:
          'The compare-against-spec step usually waits until material is at the dock. If chemistry is out of range, the rework cost is already incurred — sometimes it has already been used in production.',
      },
      {
        title: 'Audit-grade traceability gaps',
        description:
          'Regulatory audits demand a defensible chain from received doc → extracted value → acceptance decision. Spreadsheet-based workflows can not produce this without weeks of forensic reconstruction.',
      },
    ],
    features: [
      {
        title: 'Mill- and lab-specific annotations',
        description:
          'Annotate 5-10 MTCs per major producer to teach DocumentIQ the layout. The model generalizes across new revisions and works on photographed/scanned originals.',
      },
      {
        title: 'Automatic spec comparison',
        description:
          'Define your acceptance specs once. Every extracted MTC is auto-compared against those specs and flagged green/yellow/red. QC only reviews flagged certificates.',
      },
      {
        title: 'Chemistry + mechanical extraction',
        description:
          'Extract element percentages, yield strength, tensile, hardness, elongation — whatever the spec demands. Even when tables span pages or use unit suffixes inconsistently.',
      },
      {
        title: 'Audit-grade evidence chain',
        description:
          'Every extracted value links back to the source PDF page and bounding box. Confidence scores are preserved. Auditors get a complete, queryable record of why every shipment was accepted or rejected.',
      },
    ],
    caseStudySlugs: ['quality-certificate-compliance'],
    documentTypeSlugs: ['mill-test-certificates', 'spec-sheets'],
    industrySlugs: ['manufacturing'],
    stats: [
      { value: '20 min → 30 sec', label: 'QC review per certificate' },
      { value: '99.5%', label: 'Field-level accuracy' },
      { value: '100%', label: 'Audit-grade traceability' },
    ],
    relatedLinks: [
      { title: 'AI Consulting Services', url: 'https://algoscale.com/ai-consulting-service/', description: 'End-to-end AI strategy for QC automation' },
      { title: 'Data Engineering Services', url: 'https://algoscale.com/data-engineering-services/', description: 'Connect extraction to your QMS or LIMS' },
      { title: 'Generative AI Services', url: 'https://algoscale.com/generative-ai-services/', description: 'Apply LLMs to regulated-industry document workflows' },
      { title: 'Custom Software Development', url: 'https://algoscale.com/custom-software-development-services/', description: 'Build a unified material-receipt record across MTCs, COAs, and POs' },
    ],
  },
  {
    slug: 'claims-processing',
    name: 'Claims Processing',
    headline: 'Extract loss-event data from FNOL forms and supporting docs in minutes, not days',
    description:
      'Pull policy numbers, loss dates, damage descriptions, and dollar amounts from First Notice of Loss forms, police reports, and supporting evidence — automating triage and routing to the right adjuster instantly.',
    heroDescription:
      'Claims intake is where insurers leak both time and accuracy. A First Notice of Loss arrives in a dozen formats — agent intake forms, broker emails, customer PDFs, police reports — and a junior adjuster has to read each one, key the data into the claims system, and route to the right specialist team. The slowest part of the entire claims lifecycle is the data entry on day one. DocumentIQ extracts policy number, loss date, claimant info, loss description, severity indicators, and supporting evidence references from every intake document and feeds the structured record straight into the claims platform. Adjusters start working the claim within minutes of receipt.',
    painPoints: [
      {
        title: 'FNOL data trapped in inconsistent intake formats',
        description:
          'Agents submit on their forms. Brokers email PDFs. Customers fill web intake or send phone notes. Every channel has different fields in different orders, and a junior adjuster has to normalize manually.',
      },
      {
        title: 'Supporting evidence comes in late',
        description:
          'Police reports, repair estimates, and medical records arrive over the next 10-30 days. Each one needs to be linked to the claim, parsed for relevant fields, and routed. Late evidence delays settlement.',
      },
      {
        title: 'Severity routing is reactive',
        description:
          'Without structured loss data at intake, claims go to a generic queue and get reassigned later when severity is understood. The hand-off cost compounds, and the customer waits.',
      },
      {
        title: 'Audit trails for regulators',
        description:
          'Property & casualty regulators expect every claim decision to be defensible. Spreadsheets and email threads do not provide the field-level traceability auditors demand.',
      },
    ],
    features: [
      {
        title: 'Multi-channel FNOL ingest',
        description:
          'PDF intake forms, broker emails, scanned customer submissions, structured web forms — all flow into the same extraction pipeline. The model handles layout variance automatically.',
      },
      {
        title: 'Loss-event field extraction',
        description:
          'Policy number, loss date, loss location, claimant info, loss description, severity indicators, estimated value. Annotation training handles carrier-specific intake form layouts.',
      },
      {
        title: 'Evidence linking',
        description:
          'Police reports, repair estimates, medical records — extract relevant fields and link them to the existing claim record automatically. Adjusters see the complete picture without manual reconciliation.',
      },
      {
        title: 'Auto-routing by severity',
        description:
          'Use extracted loss value, peril type, and damage indicators to route claims to the right specialist queue at intake — instead of after a junior adjuster manually triages.',
      },
    ],
    caseStudySlugs: [],
    documentTypeSlugs: ['insurance-claims', 'financial-statements'],
    industrySlugs: ['financial-services'],
    stats: [
      { value: '90%+', label: 'FNOL fields auto-extracted' },
      { value: '4 hours → 15 min', label: 'Intake-to-adjuster handoff' },
      { value: '100%', label: 'Audit-grade traceability' },
    ],
    relatedLinks: [
      { title: 'Insurance Software Development', url: 'https://algoscale.com/insurance-software-development-services/', description: 'Automate claims processing and policy document extraction' },
      { title: 'AI Consulting Services', url: 'https://algoscale.com/ai-consulting-service/', description: 'End-to-end AI strategy for claims modernization' },
      { title: 'Generative AI Services', url: 'https://algoscale.com/generative-ai-services/', description: 'Apply LLMs to P&C claims workflows' },
      { title: 'Custom Software Development', url: 'https://algoscale.com/custom-software-development-services/', description: 'Integrate DocumentIQ with Guidewire, Duck Creek, or your in-house claims platform' },
    ],
  },
]

export const functionsBySlug = Object.fromEntries(functions.map((f) => [f.slug, f]))
